import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  ViewChild,
  computed,
  effect,
  inject,
  signal
} from '@angular/core';
import { NgClass } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { toSignal } from '@angular/core/rxjs-interop';
import { SidebarService } from '@shared/services/sidebar.service';
import { NavLayoutService } from '@shared/services/nav-layout.service';
import { ThemeToggleButtonComponent } from '@shared/components/common/theme-toggle/theme-toggle-button.component';
import { UserDropdownComponent } from '@shared/components/header/user-dropdown/user-dropdown.component';
import { BadgeComponent } from '@shared/components/ui/badge/badge.component';
import { SelectSearchComponent } from '@shared/components/form/select-search/select-search.component';
import { NotificationIconComponent } from '@pages/modulos/control-general/notificaciones/notification-dropdown/notification-icon.component';
import { EmpresaService } from '@pages/empresas/services/empresa.service';
import { CompanyContextService } from '@pages/empresas/services/company-context.service';
import { AuthService } from '@shared/services/auth.service';
import { UsuarioService } from '@shared/services/usuario/usuario.service';
import { PermissionService } from '@shared/services/permission/permission.service';
import { CurrentPlanService, CurrentPlanSubscription, isInTrial, trialDaysLeftOf } from '@shared/services/plan/current-plan.service';
import { EmpresaDTO } from '@pages/empresas/models/empresa.model';

const DEFAULT_LOGO = 'assets/images/Partner-Core-No-Image.webp';

@Component({
  selector: 'app-header',
  imports: [
    NgClass,
    FormsModule,
    RouterModule,
    ThemeToggleButtonComponent,
    NotificationIconComponent,
    UserDropdownComponent,
    BadgeComponent,
    SelectSearchComponent,
    TranslocoModule,
  ],
  templateUrl: './app-header.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppHeaderComponent {
  private readonly sidebarService = inject(SidebarService);
  private readonly navLayoutService = inject(NavLayoutService);
  private readonly empresaService = inject(EmpresaService);
  private readonly authService = inject(AuthService);
  private readonly companyContext = inject(CompanyContextService);
  private readonly usuarioService = inject(UsuarioService);
  private readonly permissionService = inject(PermissionService);
  private readonly currentPlan = inject(CurrentPlanService);

  /** Disposición del menú activa; en `topnav` el toggle del sidebar se oculta en escritorio. */
  readonly navLayout = this.navLayoutService.mode;

  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  // Plan activo del cliente (badge): se comparte con el perfil vía CurrentPlanService.
  readonly planPrimary  = this.currentPlan.primary;
  readonly planInTrial  = this.currentPlan.inTrial;
  readonly planTrialDays = this.currentPlan.trialDaysLeft;
  /** Suscripciones activas (CORE primero), una por producto, para el popover del badge. */
  readonly planList = this.currentPlan.active;
  /** El cliente tiene planes en más de un producto → mostrar contador "+N" y abrir el popover. */
  readonly planHasMultiple = this.currentPlan.hasMultiple;
  /** Color del badge: ámbar en prueba, azulado (brand) si está activo. */
  readonly planBadgeColor = computed<'primary' | 'warning'>(() => this.currentPlan.inTrial() ? 'warning' : 'primary');
  /** Popover de planes (desglose por producto) abierto. */
  readonly planMenuOpen = signal(false);

  // Helpers por suscripción reutilizados en el popover (estado de prueba por fila).
  readonly subInTrial = isInTrial;
  readonly subTrialDays = trialDaysLeftOf;
  /** Nombre visible del producto de una suscripción (cae al código). */
  productLabel(sub: CurrentPlanSubscription): string { return sub.productName ?? sub.productCode ?? ''; }

  togglePlanMenu(): void { this.planMenuOpen.update((v) => !v); }
  closePlanMenu(): void { this.planMenuOpen.set(false); }

  // Sidebar state as signals — subscription lifecycle managed automatically
  readonly isMobileOpen = toSignal(this.sidebarService.isMobileOpen$, { initialValue: false });
  readonly isExpanded = toSignal(this.sidebarService.isExpanded$, { initialValue: false });

  readonly isApplicationMenuOpen = computed(() => this.sidebarService.headerMenuOpen());

  // Company selector options — derived from the empresa list signal
  readonly companyOptions = computed(() =>
    this.empresaService.empresas().map(e => ({ value: String(e.id), label: e.companyName }))
  );

  // Selected company — derived from context (single source of truth)
  readonly selectedCompany = computed<string | null>(() => {
    const id = this.companyContext.companyId();
    return id !== null ? String(id) : null;
  });

  /**
   * Base de la ruta de Empresas accesible para el usuario (Sistema vs Control General), resuelta
   * desde sus permisos de módulo. Null si no tiene acceso a Empresas (el logo no navega entonces).
   */
  private readonly companyDetailBase = computed<string | null>(() => {
    const roleId = this.usuarioService.perfil()?.role?.id;
    if (!roleId) return null;
    if (this.permissionService.checkModulePermission(roleId, '/admin-sistema/empresas', 'view')) return '/admin-sistema/empresas';
    if (this.permissionService.checkModulePermission(roleId, '/admin/empresas', 'view')) return '/admin/empresas';
    return null;
  });

  /**
   * RouterLink al detalle de la empresa seleccionada (`<base>/<companyId>`), o null si no hay empresa
   * activa o el usuario no puede ver Empresas. La ruta destino está protegida por `permissionGuard`.
   */
  readonly companyDetailLink = computed<(string | number)[] | null>(() => {
    const base = this.companyDetailBase();
    const id = this.companyContext.companyId();
    return base && id !== null ? [base, id] : null;
  });

  // Company logo — writable signal updated by the logo effect below
  readonly imgLogo = signal(DEFAULT_LOGO);

  // Current company — reacts to both selection changes and direct data updates
  private readonly currentCompany = computed<EmpresaDTO | null>(() => {
    const id = this.companyContext.companyId();
    const updated = this.companyContext.onCompanyUpdated()();
    if (updated && (updated as EmpresaDTO).id === id) {
      return updated as EmpresaDTO;
    }
    return this.empresaService.empresas().find(e => e.id === id) ?? null;
  });

  constructor() {
    // Valida la seleccion persistida cuando carga la lista.
    // Si el ID guardado ya no existe (empresa eliminada) o no hay seleccion, usa la primera empresa.
    effect(() => {
      const list = this.empresaService.empresas();
      if (list.length === 0) return;

      const storedId = this.companyContext.companyId();
      const isValid = storedId !== null && list.some(e => e.id === storedId);

      if (!isValid) {
        this.companyContext.setCompany(list[0].id);
      }
    });

    // Load and update logo reactively
    // onCleanup cancels any in-flight request and revokes the blob URL on each re-run and on destroy
    effect(() => {
      const company = this.currentCompany();
      // logoUrl es una URL pública directa (object storage); usable en <img src>.
      this.imgLogo.set(
        company?.logoUrl && /^https?:\/\//i.test(company.logoUrl) ? company.logoUrl : DEFAULT_LOGO
      );
    });
  }

  ngOnInit(): void {
    if (this.authService.isLoggedIn()) {
      this.empresaService.loadCompaniesByClient().subscribe();
      // Plan activo del cliente para el badge (y reutilizado por el perfil). El backend lo scopea
      // al cliente de la sesión; el plan es por cliente, así que no se recarga al cambiar de empresa.
      this.currentPlan.load();
    }
  }

  onCompanySelected(id: string): void {
    this.companyContext.setCompany(Number(id));

    // Limpiar caches del rol anterior para forzar recarga con el rol de la nueva empresa
    this.permissionService.clearSidebarCache();

    // Recargar el perfil: el interceptor X-Company-Id enviará el nuevo companyId
    // y el backend devolverá el rol de la membresía en esa empresa.
    // El sidebar se recarga automáticamente vía el effect en AppSidebarMenuComponent
    // al detectar el cambio en perfil() y companyId().
    this.usuarioService.getPerfil(true).subscribe();
  }

  /**
   * Si el logo de la empresa no puede cargar (servidor caído, URL rota),
   * cae al logo por defecto de assets. Guarda contra bucle si el propio
   * default fallara.
   */
  onLogoError() {
    if (this.imgLogo() !== DEFAULT_LOGO) {
      this.imgLogo.set(DEFAULT_LOGO);
    }
  }

  /**
   * Listener de resize para sincronizar breakpoint con el servicio
   */
  @HostListener('window:resize', ['$event'])
  onWindowResize(event: Event) {
    const currentWidth = (event.target as Window).innerWidth;
    this.sidebarService.updateBreakpoint(currentWidth);
  }

  /**
   * Maneja el toggle del sidebar según el breakpoint actual
   * Usa el breakpoint del servicio para garantizar consistencia
   */
  handleToggle() {
    const breakpoint = this.sidebarService.getCurrentBreakpoint();
    
    if (breakpoint === 'desktop') {
      this.sidebarService.toggleExpanded();
    } else {
      this.sidebarService.toggleMobileOpen();
    }
  }

  /**
   * Alterna la visibilidad del menú de aplicaciones (tema, notificaciones, usuario)
   * Notifica al servicio para que el sidebar se ajuste dinámicamente
   */
  toggleApplicationMenu() {
    this.sidebarService.toggleHeaderMenu();
  }

  ngAfterViewInit() {
    document.addEventListener('keydown', this.handleKeyDown);
  }

  ngOnDestroy() {
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  handleKeyDown = (event: KeyboardEvent) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      this.searchInput?.nativeElement.focus();
    }
  };
}